import { makeStyles } from 'tss-react/mui';
import { useEntity } from '@backstage/plugin-catalog-react';
import { getColors } from '@mercedes-benz/odp-theme-v2';

// Maps a known annotation key to a display name and icon URL (or emoji fallback).
// Extend this list as new integrations are onboarded.
const ANNOTATION_TOOLS: Array<{
  annotation: string;
  name: string;
  emoji: string;
  buildUrl?: (value: string) => string;
}> = [
  {
    annotation: 'sonarqube.org/project-key',
    name: 'SonarQube',
    emoji: '🔍',
  },
  {
    annotation: 'goharbor.io/repository-slug',
    name: 'Harbor',
    emoji: '⚓',
    buildUrl: slug =>
      `https://harbor.mercedes-benz.com/harbor/projects/${slug}`,
  },
  {
    annotation: 'github.com/project-slug',
    name: 'GitHub',
    emoji: '🐙',
    buildUrl: slug => `https://github.com/${slug}`,
  },
  {
    annotation: 'datadoghq.mercedes-benz.com/enabled',
    name: 'Datadog',
    emoji: '📈',
  },
  {
    annotation: 'synopsys.com/project-key',
    name: 'Black Duck',
    emoji: '🦆',
  },
  {
    annotation: 'mercedes-benz.com/sechub-project-key',
    name: 'SecHub',
    emoji: '🛡️',
  },
];

const useStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  return {
    root: {
      width: '100%',
    },
    headerBar: {
      width: '100%',
      background: c.background.elevated,
      borderRadius: 8,
      padding: '12px 24px',
      color: c.text.primary,
      fontWeight: 600,
      fontSize: '1rem',
      boxSizing: 'border-box' as const,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
      gap: 16,
      marginTop: 24,
    },
    toolCard: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: c.background.elevated,
      borderRadius: 12,
      padding: '24px 16px',
      textAlign: 'center',
      cursor: 'default',
      textDecoration: 'none',
      transition: 'background 0.15s ease',
      '&:hover': {
        background: c.border.default,
      },
    },
    toolCardLink: {
      cursor: 'pointer',
    },
    toolIcon: {
      fontSize: '2rem',
      marginBottom: 8,
      lineHeight: 1,
    },
    toolLabel: {
      fontSize: '0.8rem',
      color: fgAlpha(0.7),
    },
    emptyState: {
      background: c.background.paper,
      borderRadius: 12,
      border: `1px solid ${c.border.default}`,
      padding: 24,
      minHeight: 200,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      textAlign: 'center',
      marginTop: 24,
    },
    emptyIcon: {
      fontSize: 36,
      opacity: 0.3,
    },
    emptyTitle: {
      color: c.text.primary,
      fontWeight: 600,
      fontSize: '1rem',
      margin: 0,
    },
    emptySubtitle: {
      color: fgAlpha(0.45),
      fontSize: '0.875rem',
      margin: 0,
    },
  };
});

export const ThirdPartyTab = () => {
  const { classes, cx } = useStyles();
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};

  const detectedTools = ANNOTATION_TOOLS.flatMap(tool => {
    const value = annotations[tool.annotation];
    if (!value || value === 'false') return [];
    return [{ ...tool, value }];
  });

  return (
    <div className={classes.root}>
      <div className={classes.headerBar}>3rd Parties</div>

      {detectedTools.length === 0 ? (
        <div className={classes.emptyState}>
          <span className={classes.emptyIcon}>🔌</span>
          <h3 className={classes.emptyTitle}>
            No 3rd party integrations defined
          </h3>
          <p className={classes.emptySubtitle}>
            Add known tool annotations to this entity's catalog-info.yaml to see
            integrations here.
          </p>
        </div>
      ) : (
        <div className={classes.grid}>
          {detectedTools.map(tool => {
            const url = tool.buildUrl?.(tool.value);
            return url ? (
              <a
                key={tool.annotation}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={cx(classes.toolCard, classes.toolCardLink)}
              >
                <span className={classes.toolIcon}>{tool.emoji}</span>
                <span className={classes.toolLabel}>{tool.name}</span>
              </a>
            ) : (
              <div key={tool.annotation} className={classes.toolCard}>
                <span className={classes.toolIcon}>{tool.emoji}</span>
                <span className={classes.toolLabel}>{tool.name}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
